// src/pages/ProductList.tsx - LISTADO DE PRODUCTOS CON FILTROS
import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Search, Filter, ShoppingCart,  Grid, List } from 'lucide-react';
import { products } from '../data/products';
import { ProductCategory } from '../types';
import { useCart } from '../contexts/CartContext';

const ProductList: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<ProductCategory | 'all'>('all');
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 2000]);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [sortBy, setSortBy] = useState<'name' | 'price' | 'rating'>('name');
  const { addItem } = useCart();

  const categories: { value: ProductCategory | 'all'; label: string }[] = [
    { value: 'all', label: 'Todas las categorías' },
    { value: 'paneles', label: 'Paneles Solares' },
    { value: 'inversores', label: 'Inversores' },
    { value: 'baterias', label: 'Baterías' },
    { value: 'cables', label: 'Cables' },
    { value: 'iluminacion', label: 'Iluminación' },
    { value: 'domotica', label: 'Domótica' },
    { value: 'antenas', label: 'Antenas' }
  ];

  const filteredProducts = useMemo(() => {
    let filtered = products.filter(product => {
      const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           product.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = selectedCategory === 'all' || product.category === selectedCategory;
      const matchesPrice = product.price >= priceRange[0] && product.price <= priceRange[1];
      
      return matchesSearch && matchesCategory && matchesPrice;
    });

    // Ordenar
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'price':
          return a.price - b.price;
        default:
          return a.name.localeCompare(b.name);
      }
    });

    return filtered;
  }, [searchTerm, selectedCategory, priceRange, sortBy]);

  const handleAddToCart = (e: React.MouseEvent, productId: string) => {
    e.preventDefault();
    e.stopPropagation();
    const product = products.find(p => p.id === productId);
    if (product) {
      addItem(product);
      // Aquí podrías mostrar una notificación
    }
  };

  return (
    <div className="min-h-screen bg-infinity-black">
      {/* Hero Section */}
      <section className="bg-infinity-gradient text-infinity-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Nuestros Productos</h1>
          <p className="text-xl">Soluciones completas en energía renovable y tecnología</p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Filtros Laterales */}
          <div className="lg:w-1/4">
            <div className="bg-infinity-dark rounded-lg shadow-lg p-6 sticky top-4">
              <h3 className="text-lg font-semibold text-infinity-yellow mb-4 flex items-center">
                <Filter size={20} className="mr-2" />
                Filtros
              </h3>

              {/* Búsqueda */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-yellow-500 mb-2">
                  Buscar producto
                </label>
                <div className="relative">
                  <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Buscar..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-infinity-blue rounded-lg focus:ring-2 focus:ring-infinity-blue focus:border-transparent bg-infinity-white text-infinity-dark"
                  />
                </div>
              </div>

              {/* Categorías */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-yellow-500 mb-2">
                  Categoría
                </label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value as ProductCategory | 'all')}
                  className="w-full p-2 border border-infinity-dark rounded-lg focus:ring-2 focus:ring-infinity-dark focus:border-transparent bg-infinity-white text-infinity-dark"
                >
                  {categories.map(category => (
                    <option key={category.value} value={category.value}>
                      {category.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Rango de Precio */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-yellow-500 mb-2">
                  Rango de Precio: S/ {priceRange[0]} - S/ {priceRange[1]}
                </label>
                <input
                  type="range"
                  min="0"
                  max="2000"
                  value={priceRange[1]}
                  onChange={(e) => setPriceRange([priceRange[0], parseInt(e.target.value)])}
                  className="w-full"
                />
              </div>
            </div>
          </div>

          {/* Lista de Productos */}
          <div className="lg:w-3/4">
            {/* Barra de herramientas */}
            <div className="bg-infinity-dark rounded-lg shadow-lg p-4 mb-6">
              <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                <div className="flex items-center gap-4">
                  <span className="text-gray-200">
                    {filteredProducts.length} productos encontrados
                  </span>
                </div>
                
                <div className="flex items-center gap-4">
                  {/* Ordenar */}
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as 'name' | 'price' | 'rating')}
                    className="px-3 py-2 border border-infinity-blue rounded-lg focus:ring-2 focus:ring-infinity-blue focus:border-transparent bg-infinity-white text-infinity-dark"
                  >
                    <option value="name">Ordenar por nombre</option>
                    <option value="price">Ordenar por precio</option>
                    <option value="rating">Ordenar por rating</option>
                  </select>

                  {/* Vista */}
                  <div className="flex rounded-lg border border-gray-300 overflow-hidden">
                    <button
                      onClick={() => setViewMode('grid')}
                      className={`p-2 ${viewMode === 'grid' ? 'bg-infinity-yellow text-infinity-black' : 'bg-infinity-dark text-infinity-white'}`}
                    >
                      <Grid size={20} />
                    </button>
                    <button
                      onClick={() => setViewMode('list')}
                      className={`p-2 ${viewMode === 'list' ? 'bg-infinity-yellow text-infinity-black' : 'bg-infinity-dark text-infinity-white'}`}
                    >
                      <List size={20} />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Grid/Lista de Productos */}
            <div className={viewMode === 'grid' 
              ? 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6' 
              : 'space-y-4'
            }>
              {filteredProducts.map((product) => (
                <Link
                  key={product.id}
                  to={`/productos/${product.id}`}
                  className={`bg-infinity-dark rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow ${
                    viewMode === 'list' ? 'flex' : ''
                  }`}
                >
                  <div className={viewMode === 'list' ? 'w-48 h-32' : 'h-48'}>
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  
                  <div className={`p-4 ${viewMode === 'list' ? 'flex-1' : ''}`}>
                    {/* Eliminar rating visual */}
                    
                    <h3 className="text-lg font-semibold text-infinity-yellow mb-2">
                      {product.name}
                    </h3>
                    <p className="text-infinity-white/80 mb-3 text-sm">
                      {product.description}
                    </p>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-xl font-bold text-infinity-yellow">
                  
                      </span>
                      <button
                        onClick={(e) => handleAddToCart(e, product.id)}
                        className="btn-primary flex items-center space-x-1 text-sm bg-infinity-yellow text-infinity-black hover:bg-infinity-purple hover:text-infinity-white"
                      >
                        <ShoppingCart size={16} />
                        <span>Agregar</span>
                      </button>
                    </div>
                    
                    {/* Mostrar advertencia si quedan pocas unidades */}
                    {typeof product.inStock === 'number' && product.inStock < 10 && (
                      <p className="text-infinity-yellow text-xs mt-2">
                        Solo quedan {product.inStock} unidades
                      </p>
                    )}
                  </div>
                </Link>
              ))}
            </div>

            {filteredProducts.length === 0 && (
              <div className="text-center py-12">
                <p className="text-infinity-white/60 text-lg">No se encontraron productos con los filtros seleccionados</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductList;
